import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { CustomEvent } from "schemas/functions";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

import VAUserDrawer from "./VAUserDrawer";
import styles from "./styles";

const PER_PAGE = 100;

interface VAUser {
  organization_id: number;
  name: string;
  emails_scheduled: number;
  expected_emails: number;
  email_gap: number;
}

interface VAUserNoEmails {
  organization_id: number;
  name: string;
  email: string;
  va_status: string;
  subscription_status: string;
}

export interface BentoCategoryItem {
  id: number;
  name: string;
  displayName: string;
}

const VAManagement = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [tab, setTab] = useState<string>("sent-emails");
  const [loading, setLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [weekSelection, setWeekSelection] = useState<string>("next_week");
  const [categoriesById, setCategoriesById] = useState<Record<number, string>>(
    {},
  );

  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<number>>(new Set());
  const [scheduling, setScheduling] = useState(false);

  // Sent Emails tab state
  const [sentPage, setSentPage] = useState<number>(1);
  const [sentResults, setSentResults] = useState<VAUser[]>([]);
  const [sentTotal, setSentTotal] = useState<number>(0);

  // No Emails tab state
  const [noEmailsPage, setNoEmailsPage] = useState<number>(1);
  const [noEmailsResults, setNoEmailsResults] = useState<VAUserNoEmails[]>([]);
  const [noEmailsTotal, setNoEmailsTotal] = useState<number>(0);

  const fetchSentEmails = async (
    page: number,
    week: string = weekSelection,
  ) => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users?page=${page}&per_page=${PER_PAGE}&week_selection=${week}`,
        "GET",
      );
      setSentResults(res.users);
      setSentTotal(res.total);
    } catch (error: any) {
      setAlert(error?.message || "An error occurred", "warning");
    } finally {
      setLoading(false);
    }
  };

  const fetchNoEmails = async (page: number) => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/no-emails?page=${page}&per_page=${PER_PAGE}`,
        "GET",
      );
      setNoEmailsResults(res.users);
      setNoEmailsTotal(res.total);
    } catch (error: any) {
      setAlert(error?.message || "An error occurred", "warning");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/bento-categories",
        "GET",
      );
      const map: Record<number, string> = {};
      for (const cat of res.bentoCategories as BentoCategoryItem[]) {
        map[cat.id] = cat.displayName;
      }
      setCategoriesById(map);
    } catch {
      // Non-critical — category names will just show IDs as fallback
    }
  };

  useEffect(() => {
    fetchSentEmails(1);
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
    if (newValue === "sent-emails") {
      setSentPage(1);
      fetchSentEmails(1);
    } else if (newValue === "no-emails") {
      setNoEmailsPage(1);
      fetchNoEmails(1);
    }
  };

  const handleWeekChange = (
    _: React.MouseEvent<HTMLElement>,
    newWeek: string | null,
  ) => {
    if (!newWeek) return;
    setWeekSelection(newWeek);
    setSelectedOrgIds(new Set());
    setSentPage(1);
    setNoEmailsPage(1);
    if (tab === "sent-emails") {
      fetchSentEmails(1, newWeek);
    }
  };

  const handleSentPageChange = (_: CustomEvent, newPage: number) => {
    setSentPage(newPage + 1);
    fetchSentEmails(newPage + 1);
  };

  const handleNoEmailsPageChange = (_: CustomEvent, newPage: number) => {
    setNoEmailsPage(newPage + 1);
    fetchNoEmails(newPage + 1);
  };

  const getPercentage = (scheduled: number, expected: number) => {
    if (expected === 0) return 0;
    return Math.round((scheduled / expected) * 100);
  };

  const toggleRow = (orgId: number) => {
    setSelectedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  const results: (VAUser | VAUserNoEmails)[] =
    tab === "sent-emails" ? sentResults : noEmailsResults;

  const allCurrentSelected =
    results.length > 0 &&
    results.every((r) => selectedOrgIds.has(r.organization_id));

  const toggleAll = () => {
    if (allCurrentSelected) {
      setSelectedOrgIds((prev) => {
        const next = new Set(prev);
        for (const row of results) {
          next.delete(row.organization_id);
        }
        return next;
      });
    } else {
      setSelectedOrgIds((prev) => {
        const next = new Set(prev);
        for (const row of results) {
          next.add(row.organization_id);
        }
        return next;
      });
    }
  };

  const select40to75 = () => {
    if (selectedOrgIds.size > 0) {
      setSelectedOrgIds(new Set());
      return;
    }

    const matching = sentResults.filter((row) => {
      const pct = getPercentage(row.emails_scheduled, row.expected_emails);
      return pct >= 40 && pct < 75;
    });
    setSelectedOrgIds((prev) => {
      const next = new Set(prev);
      for (const row of matching) {
        next.add(row.organization_id);
      }
      return next;
    });
  };

  const handleScheduleMore = async () => {
    if (selectedOrgIds.size === 0) return;
    setScheduling(true);
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/virtual-assistant-users/enqueue-schedule-more",
        "POST",
        {},
        {
          organizationIds: Array.from(selectedOrgIds),
          weekSelection: weekSelection,
        },
      );
      const parts: string[] = [];
      if (res.enqueued > 0) {
        parts.push(`${res.enqueued} enqueued`);
      }
      if (res.alreadyScheduled > 0) {
        parts.push(`${res.alreadyScheduled} already scheduled today`);
      }
      setAlert(parts.join(", ") || "No organizations to schedule", "success");
      setSelectedOrgIds(new Set());
    } catch (error: any) {
      setAlert(error?.message || "Failed to enqueue scheduling", "warning");
    } finally {
      setScheduling(false);
    }
  };

  return (
    <StyledPage
      content={
        <>
          <Backdrop sx={styles.backdrop} open={loading}>
            <CircularProgress />
          </Backdrop>

          <Grid sx={{ mt: 2 }}>
            <Typography variant="h3" gutterBottom>
              <strong>VA Management</strong>
            </Typography>
          </Grid>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab label="Sent Emails" value="sent-emails" />
              <Tab label="Sent No Emails" value="no-emails" />
            </Tabs>
            {tab === "sent-emails" && (
              <ToggleButtonGroup
                value={weekSelection}
                exclusive
                onChange={handleWeekChange}
                size="small"
              >
                <ToggleButton value="this_week">This Week</ToggleButton>
                <ToggleButton value="next_week">Next Week</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            {tab === "sent-emails" && (
              <Button variant="outlined" size="small" onClick={select40to75}>
                Select 40-75%
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              disabled={selectedOrgIds.size === 0 || scheduling}
              onClick={handleScheduleMore}
            >
              {scheduling
                ? "Scheduling..."
                : `Schedule More (${selectedOrgIds.size})`}
            </Button>
            {tab === "no-emails" && (
              <ToggleButtonGroup
                value={weekSelection}
                exclusive
                onChange={handleWeekChange}
                size="small"
              >
                <ToggleButton value="this_week">This Week</ToggleButton>
                <ToggleButton value="next_week">Next Week</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {tab === "sent-emails" && (
            <>
              <TableContainer sx={styles.tableContainer}>
                <Table stickyHeader sx={styles.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allCurrentSelected}
                          indeterminate={
                            selectedOrgIds.size > 0 && !allCurrentSelected
                          }
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell>Org ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Emails Scheduled</TableCell>
                      <TableCell>Expected Emails</TableCell>
                      <TableCell>Email Gap</TableCell>
                      <TableCell>% Scheduled</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sentResults.map((row) => {
                      const pct = getPercentage(
                        row.emails_scheduled,
                        row.expected_emails,
                      );
                      const isHealthy = pct >= 75;
                      const isSelected = selectedOrgIds.has(
                        row.organization_id,
                      );
                      return (
                        <TableRow
                          key={row.organization_id}
                          selected={isSelected}
                          sx={{
                            ...(isHealthy
                              ? styles.greenRow
                              : styles.warningRow),
                            ...styles.clickableRow,
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleRow(row.organization_id)}
                            />
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.organization_id}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.name}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.emails_scheduled}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.expected_emails}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.email_gap}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {pct}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={sentTotal}
                rowsPerPage={PER_PAGE}
                rowsPerPageOptions={[PER_PAGE]}
                page={sentPage - 1}
                onPageChange={handleSentPageChange}
              />
            </>
          )}

          {tab === "no-emails" && (
            <>
              <TableContainer sx={styles.tableContainer}>
                <Table stickyHeader sx={styles.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allCurrentSelected}
                          indeterminate={
                            selectedOrgIds.size > 0 && !allCurrentSelected
                          }
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell>Org ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>VA Status</TableCell>
                      <TableCell>Subscription Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {noEmailsResults.map((row) => {
                      const isSelected = selectedOrgIds.has(
                        row.organization_id,
                      );
                      return (
                        <TableRow
                          key={row.organization_id}
                          sx={styles.clickableRow}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleRow(row.organization_id)}
                            />
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.organization_id}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.name}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.email}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.va_status}
                          </TableCell>
                          <TableCell
                            onClick={() =>
                              setSelectedOrgId(row.organization_id)
                            }
                          >
                            {row.subscription_status}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={noEmailsTotal}
                rowsPerPage={PER_PAGE}
                rowsPerPageOptions={[PER_PAGE]}
                page={noEmailsPage - 1}
                onPageChange={handleNoEmailsPageChange}
              />
            </>
          )}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />

          <VAUserDrawer
            organizationId={selectedOrgId}
            onClose={() => setSelectedOrgId(null)}
            setAlert={setAlert}
            categoriesById={categoriesById}
          />
        </>
      }
    />
  );
};

export default VAManagement;
